import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { percentToTimestamp, useTimelineDrag } from '../../../hooks/useTimelineDrag';
import { ChatTimelineEvent, Timeline } from '../../../parser';

export const ChatTrack = ({
    timeline,
    duration,
    isActive,
    onClick
}: {
    timeline: Timeline;
    duration: Duration;
    isActive: boolean;
    onClick: (event: ChatTimelineEvent) => void;
}) => {
    const [chat, setChat] = useState<ChatTimelineEvent[]>([]);

    useEffect(() => {
        if (!timeline?.chat || duration.toMillis() === 0) {
            setChat([]);
            return;
        }

        setChat(timeline.chat);
    }, [timeline?.chat, duration]);

    const drag = useTimelineDrag<ChatTimelineEvent>(
        (chatItem, position) => {
            // Video time works in seconds, so we need to round the timestamp to the nearest second
            chatItem.timestamp = percentToTimestamp(duration, position);
            // Check if there are two chat messages with the same timestamp
            const duplicate = timeline.chat.find((c) => c.timestamp.equals(chatItem.timestamp) && c !== chatItem);
            if (!!duplicate) {
                if (chatItem.timestamp.as('seconds') > 0) {
                    chatItem.timestamp = chatItem.timestamp.minus({ seconds: 1 });
                } else {
                    chatItem.timestamp = chatItem.timestamp.plus({ seconds: 1 });
                }
            }
            setChat([...timeline.chat.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())]);
        },
        (chatItem) => {
            chatItem.changed = true;
            timeline.changed = true;
            timeline.chat = [...timeline.chat];
        }
    );

    const onDragStart = (e: React.MouseEvent<HTMLButtonElement>, chatItem: ChatTimelineEvent) =>
        drag.begin(e, chatItem, (chatItem.timestamp.toMillis() / duration.toMillis()) * 100);

    return (
        <div className={`track track-chat ${isActive ? 'track-active' : ''} row-start-6`}>
            {chat.map((chatItem, index) => {
                return (
                    <div
                        key={index}
                        className={chatItem.changed ? 'changed' : ''}
                        style={{ left: `${(chatItem.timestamp.toMillis() / duration.toMillis()) * 100}%` }}
                        onClick={() => onClick(chatItem)}
                    >
                        <button onMouseDown={(event) => onDragStart(event, chatItem)}></button>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="black"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="size-6 mx-2 stroke-current"
                        >
                            <path d="M5 7C3.89543 7 3 7.89543 3 9V16C3 17.1046 3.89543 18 5 18H18C19.1046 18 20 17.1046 20 16V10L23 7H5Z" />
                            <circle cx="11.5" cy="12.5" r="0.5" />
                            <circle cx="7.5" cy="12.5" r="0.5" />
                            <circle cx="15.5" cy="12.5" r="0.5" />
                        </svg>
                    </div>
                );
            })}
        </div>
    );
};
